import { getI18n } from "react-i18next";
import { get } from "lodash";
import { http } from "../../../utils/axios/axios";
import {
  CreateNPAData,
  FormAction,
  OnboardingExam,
  OnboardingFormData,
  OnboardingQuiz,
  OnboardingQuizBase,
} from "../types";
import {
  COMMON_PUBLIC_FRONTEND_ENDPOINT,
  CORE_URL,
  messagingUrl,
  baseURL,
  fakeToken,
  getToken,
} from "../utils";
import { DeleteFile, DownLoadFile, FileUpload } from "./file-op-interface";
import store from "../../../redux/store";
import { PruToast } from "../../../components/pru-toast";
import { ROOT_PATH, ONBOARDING_PATH } from "../../../routes/constants";
import { handleSurveyError } from "./network.util";

// Helper function to retry getting the token
const retryGetToken = (maxRetries = 3, interval = 500) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    let retryTimeout: NodeJS.Timeout;

    const attempt = () => {
      const token = getToken();
      if (token !== fakeToken) {
        clearTimeout(retryTimeout); // Clear the timeout if token is received
        resolve(token); // Resolve the promise with the token
      } else if (attempts >= maxRetries) {
        reject(new Error("Failed to retrieve token after maximum attempts.")); // Reject the promise after max retries
      } else {
        attempts++;
        retryTimeout = setTimeout(attempt, interval); // Set timeout for next attempt
      }
    };

    attempt(); // Initiate the first token retrieval attempt
  });
};

// Enhanced getCustomHeader function with retry logic
export const getCustomHeaderAsync = async (): Promise<any> => {
  try {
    const token = await retryGetToken(); // Attempt to get the token with retry logic
    return {
      "Ocp-Apim-Subscription-Key": get(window, "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY", ""),
      Authorization: token,
      "Accept-Language": mapLanguage(getI18n().language),
    };
  } catch (error) {
    // Handle the error as desired, e.g., return error headers or throw the error
    return {};
  }
};

export const getCustomHeader = () => {
  return {
    "Ocp-Apim-Subscription-Key": get(window, "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY", ""),

    Authorization: getToken(),
    "Accept-Language": mapLanguage(getI18n().language),
  };
};

export function mapSurveyLanguage(lang: string): string {
  const langkey = lang.toLocaleLowerCase();
  const langMapping: Record<string, string> = {
    "zh-hant": "zh-tw",
    zhhk: "zh-tw",
  };
  return langMapping[langkey] || langkey;
}
export function mapLanguage(lang: string): string {
  const langkey = lang.toLocaleLowerCase();
  const langMapping: Record<string, string> = {
    zhhk: "zh-Hant",
  };
  return langMapping[langkey] || langkey;
}
export const fetchOnboardingFormTemplate = async (params: any): Promise<any> => {
  const language = mapSurveyLanguage(getI18n().language ?? "en");
  const response = await http.get(`${baseURL}/onboarding/form-template`, {
    headers: await getCustomHeaderAsync(),
    params: {
      ...params,
      language,
    },
  });
  return response;
};

export const fetchCandidateOnboardingForm = async (candidateId: string): Promise<any> => {
  // const form = await import("./form.json");
  // return { data: {} };
  const response = await http.get(`${baseURL}/onboarding/forms?candidateId=${candidateId}`, {
    headers: await getCustomHeaderAsync(),
  });
  return response;
};

export const submitApplicationForm = async (
  data: OnboardingFormData,
  action?: FormAction,
): Promise<any> => {
  const language = mapSurveyLanguage(getI18n().language ?? "en");
  const actionQuery = action ? `formAction=${action}` : "";
  const response = await http
    .post(`${baseURL}/onboarding/forms?${actionQuery}&lang=${language}`, data, {
      headers: { ...(await getCustomHeaderAsync()), __loading: true },
    })
    .then(
      (res) => res,
      (err) => {
        return err?.response?.data ?? { errors: [{ message: "Unknown error" }], statusCode: 500 };
      },
    );
  return response;
};

export const fetchOnboardingFormDeriveData = async (params: any): Promise<any> => {
  if (params.triggerKey === "agentProfile") {
    params.agentCode = store.getState()?.onboarding?.candidateOnboardingForm?.recruiterCode;
  }
  const url = new URL(`${baseURL}${params.path}?triggerKey=${params.triggerKey}`);
  if (params.agentCode) {
    url.searchParams.append("agentCode", params.agentCode);
  }
  if (params.derivedValue) {
    url.searchParams.append("derivedValue", params.derivedValue);
  }
  const response = await http.get(url.href, {
    headers: await getCustomHeaderAsync(),
  });
  return response;
};

export const fetchConfig = async (): Promise<any> => {
  const response = await http.get(
    `${CORE_URL}/configs/recruitment-common?group=pruforce.Recruitment&namespace=default`,
    {
      headers: await getCustomHeaderAsync(),
    },
  );
  return response;
};

export const fetchGlobalConfig = async (): Promise<any> => {
  const response = await http.get(
    `/configs/global-system?group=pruforce.Global&namespace=default&access=public`,
    {
      headers: await getCustomHeaderAsync(),
    },
  );
  return response;
};

export const uploadFile = async (body: FileUpload): Promise<any> => {
  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === "file") {
      formData.append("file", value as Blob, body.fileName);
    } else {
      formData.append(key, String(value));
    }
  });
  const response = await http.post(`${baseURL}/onboarding/file/binary`, formData, {
    headers: await getCustomHeaderAsync(),
  });
  return response;
};

export const getTodoTasks = async (params: {
  applicationId: string;
  page?: number;
  limit?: number;
}): Promise<any> => {
  const { applicationId, page = 0, limit = 10 } = params;
  /**
   * disable first
   */
  const response = await http.get(`${baseURL}/onboarding/todo-task`, {
    /**
     * for self test
     */
    // const response = await http.get(`http://localhost:3000/onboarding/todo-task`, {
    headers: await getCustomHeaderAsync(),
    params: {
      applicationId,
      page: page + 1,
      type: "FOLLOWUP_REQUIRED", // PENDING_SIGNATURE | FOLLOWUP_REQUIRED
      /**
       * for self test
       */
      // agentCode: "00010009",
      limit,
    },
  });
  return response;
};
export const downFile = async (body: DownLoadFile): Promise<any> => {
  const response = await http.get(`${baseURL}/onboarding/file`, {
    headers: await getCustomHeaderAsync(),
    params: body,
  });
  return response;
};
export const deleteFile = async (body: DeleteFile): Promise<any> => {
  const response = await http.delete(`${baseURL}/onboarding/file`, {
    headers: await getCustomHeaderAsync(),
    params: body,
  });
  return response;
};

export async function getInvitationStatus(id: string): Promise<any> {
  const url = `${COMMON_PUBLIC_FRONTEND_ENDPOINT}/api/recruitment/pca/invitations/${id}`;
  return http
    .get(url, { headers: { ...(await getCustomHeaderAsync()), __loading: true } })
    .then((res: any) => res);
}

export async function inviteCandidate(param: any, navigate: any) {
  const regionCode = Array.isArray(param.regionCode) ? param.regionCode[0] : param.regionCode;

  const data = {
    firstname: param.firstname,
    lastname: param.lastname,
    gender: param.gender,
    identityDocumentType: param.identityDocumentType,
    identityDocumentNumber: param.identityDocumentNumber,
    // phone: `${regionCode} ${param.phoneNumber.replace(/ /g, "")}`, // phone can't change here, so we don't need transfer it
    email: param.email,
    languagePreference: param.languagePreference,
    reviewedAndAgreeToPICS: true,
    pageRoute: "recruitmentcandidateprofile",
  };
  const url = `${COMMON_PUBLIC_FRONTEND_ENDPOINT}/api/recruitment/invitations/${param.inviteId}/candidates`;
  http
    .post(url, data, { headers: { "agent-code": param.agentCode } })
    .then((res) => {
      PruToast({ message: getI18n().t("Submit successfully") });
      setTimeout(() => {
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-done`);
      }, 2000);
    })
    .catch((err) => {
      console.log("err===", err);
      const errorKey = get(err, "response.data.message");
      if (errorKey) {
        const msg = getI18n().t(`Recruitment.${errorKey}`);
        if (msg && !msg.startsWith("Recruitment.")) {
          PruToast({ message: msg });
          return;
        }
      }
      if (err.status === 409) {
        PruToast({ message: getI18n().t("You have been the candidate. Please don't resubmit.") });
      } else if (err.status === 400) {
        PruToast(err?.data?.message);
      } else if (err.status === 410) {
        PruToast({ message: getI18n().t("Invitation Link Expired") });
      } else if (err.status === 412) {
        PruToast({ message: getI18n().t("You are already a PRU agent.") });
      }
    });
}

export async function inviteCandidateEdit(param: any, navigate: any) {
  const data = {
    firstname: param.firstname,
    lastname: param.lastname,
    gender: param.gender,
    email: param.email,
    languagePreference: param.languagePreference,
    reviewedAndAgreeToPICS: true,
    pageRoute: "recruitmentcandidateprofile",
  };
  const url = `${COMMON_PUBLIC_FRONTEND_ENDPOINT}/api/recruitment/invitations/${param.inviteId}/candidates`;
  http
    .patch(url, data, { headers: { "agent-code": param.agentCode } })
    .then((res) => {
      PruToast({ message: getI18n().t("Submit successfully") });
      setTimeout(() => {
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-done`);
      }, 2000);
    })
    .catch((err) => {
      console.log("err===", err);
      const errorKey = get(err, "response.data.message");
      if (errorKey) {
        const msg = getI18n().t(`Recruitment.${errorKey}`);
        if (msg && !msg.startsWith("Recruitment.")) {
          PruToast({ message: msg });
          return;
        }
      }
      if (err.status === 400) {
        PruToast(err?.data?.message);
      } else if (err.status === 410) {
        PruToast({ message: getI18n().t("Invitation Link Expired") });
      } else if (err.status === 412) {
        PruToast({ message: getI18n().t("You are already a PRU agent.") });
      }
    });
}
export const quizAnswerUpdate = async (quiz: OnboardingQuiz, body: any): Promise<any> => {
  const data = {
    questionName: body.name,
    lang: mapLanguage(getI18n().language),
    answers: [body.value],
    endQuiz: body.endQuiz,
    lessonId: quiz.lessonId,
  };
  const response = await http.post(
    `${baseURL}/onboarding/training/candidates/${quiz.candidateId}/courses/${quiz.courseId}/quizzes/${quiz.quizId}/answers`,
    data,
    {
      headers: await getCustomHeaderAsync(),
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const lessonStatusUpdate = async (body: any): Promise<any> => {
  const data = {
    status: body.status,
  };
  const response = await http.patch(
    `${baseURL}/onboarding/training/candidates/${body.candidateId}/courses/${body.courseId}/lessons/${body.lessonId}/status`,
    data,
    {
      headers: await getCustomHeaderAsync(),
    },
  );
  return response;
};

export const fetchTrainingCourses = async ({
  candidateId,
}: {
  candidateId: string;
}): Promise<any> => {
  // const courses = await import("./courses.json");
  // return courses;
  const response = await http.get(
    `${baseURL}/onboarding/training/candidates/${candidateId}/courses`,
    {
      headers: { ...(await getCustomHeaderAsync()), __loading: true },
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const fetchTrainingCourseQuizDetail = async ({
  courseId,
  quizId,
  lessonId,
  candidateId,
}: OnboardingQuizBase & { candidateId: string }): Promise<any> => {
  // const quizDetail = await import("./quiz-content.json");
  // return quizDetail;
  const response = await http.get(
    `${baseURL}/onboarding/training/candidates/${candidateId}/courses/${courseId}/quizzes/${quizId}/detail?lessonId=${lessonId}`,
    {
      headers: { ...(await getCustomHeaderAsync()), __loading: true },
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const updateCourseQuizRandomQuestion = async ({
  courseId,
  quizId,
  lessonId,
  candidateId,
}: OnboardingQuizBase & { candidateId: string }): Promise<any> => {
  const response = await http.patch(
    `${baseURL}/onboarding/training/candidates/${candidateId}/courses/${courseId}/quizzes/${quizId}/questions?lessonId=${lessonId}`,
    {},
    {
      headers: { ...(await getCustomHeaderAsync()), __loading: true },
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const getOnboardingTrainingSummary = async ({
  candidateId,
}: {
  candidateId: string;
}): Promise<any> => {
  const response = await http.get(
    `${baseURL}/onboarding/training/candidates/${candidateId}/summary`,
    {
      headers: await getCustomHeaderAsync(),
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const fetchTrainingExams = async ({
  candidateId,
}: {
  candidateId: string;
}): Promise<any> => {
  // const exams = await import("./exams.json");
  // return exams;
  const response = await http.get(
    `${baseURL}/onboarding/exam/candidates/${candidateId}/registrations`,
    {
      headers: { ...(await getCustomHeaderAsync()), __loading: true },
      params: {
        language: mapLanguage(getI18n().language),
      },
    },
  );
  return response;
};

export const fetchTrainingExamCentres = async (params: OnboardingExam): Promise<any> => {
  const response = await http.get(`${baseURL}/onboarding/exam/schedules/examCentres/available`, {
    headers: await getCustomHeaderAsync(),
    params: {
      examCode: params.id,
      language: mapLanguage(getI18n().language),
    },
  });
  return response;
};

export const fetchTrainingExamTimeslots = async (
  params: OnboardingExam & { examCentre: string },
): Promise<any> => {
  const response = await http.get(`${baseURL}/onboarding/exam/schedules/timeslots/available`, {
    headers: await getCustomHeaderAsync(),
    params: {
      examCode: params.id,
      examCentre: params.examCentre,
      language: mapLanguage(getI18n().language),
    },
  });
  return response;
};

export const postCandidateExamRegistry = async (candidateId: string, body: any): Promise<any> => {
  const response = await http
    .post(`${baseURL}/onboarding/exam/candidates/${candidateId}/registrations`, body, {
      headers: await getCustomHeaderAsync(),
      params: {
        lang: mapLanguage(getI18n().language),
      },
    })
    .catch((err) => {
      return err.response.data;
    });
  return response;
};

export const postNPAToken = async (): Promise<any> => {
  const response = await http
    .post(`${baseURL}/fak/getNPAToken`, undefined, {
      headers: { ...getCustomHeader(), __loading: true },
    })
    .catch((err) => {
      return err;
    });
  return response;
};
export const postCreateNPA = async (body: CreateNPAData): Promise<any> => {
  const response = await http
    .post(`${baseURL}/fak/createNPA`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    })
    .catch((err) => {
      return err.response.data;
    });
  return response;
};
export const postPayloadTemporary = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/inputPayloadTemporary`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response;
  } catch (error: any) {
    return error.response.data;
  }
};
export const postPayloadTemporaryCandidateAddress = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/inputPayloadTemporaryCandidateAddress`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response;
  } catch (error: any) {
    return error.response.data;
  }
};
export const postInputMFJExam = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/inputMFJExam`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response;
  } catch (error: any) {
    return error.response.data;
  }
};
export const getAAJIRegistrationByCandidate = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/getAAJIRegistrationByCandidate`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response.data.body;
  } catch (error: any) {
    return error.response.data;
  }
};
export const getDocFromDB = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/getDocFromDb`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response.data.body;
  } catch (error: any) {
    return error.response.data;
  }
};
export const getFile = async (body: DownLoadFile): Promise<any> => {
  const _header = { ...(await getCustomHeaderAsync()), __loading: true };
  const response = await http
    .get(`${baseURL}/onboarding/file`, {
      headers: _header,
      params: body,
    })
    .catch((err) => {
      return err.response.data;
    });
  return response;
};

export const getAgentProfilePruOne = async (recruiterCode: string): Promise<any> => {
  const response = await http
    .get(`${baseURL}/fak/getAgentProfilePruOne/${recruiterCode}`, {
      headers: { ...getCustomHeader(), __loading: true },
    })
    .catch((err) => {
      return err.response.data;
    });
  return response;
};

export const getDetailAgentSpouse = async (spouseAgent: string): Promise<any> => {
  const response = await http
    .get(`${baseURL}/milestone/getDetailAgentSpouse/${spouseAgent}`, {
      headers: { ...getCustomHeader(), __loading: true },
    })
    .catch((err) => {
      return err.response.data;
    });
  return response.data;
};
export const updateActiveFlag = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/fak/updateActiveFlag`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response.data;
  } catch (error: any) {
    return error.response.data;
  }
};
export const cancelCandidate = async (body: any): Promise<any> => {
  try {
    const response = await http.post(`${baseURL}/prescreening/cancelCandidate`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response.data;
  } catch (error: any) {
    return error.response.data;
  }
};
export const formCancelCandidate = async (body: any): Promise<any> => {
  try {
    const response = await http.post(
      `${baseURL}/onboarding/forms?formAction=CANCEL&lang=${mapLanguage(getI18n().language)}`,
      body,
      {
        headers: { ...getCustomHeader(), __loading: true },
      },
    );
    return response.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export async function submitApplicationDetail(body: any, survey: any, navigateCallback: any) {
  const goBack = () => {
    window.ReactNativeWebView?.postMessage?.(
      `{ "action" : "onSubmit", "data": ${JSON.stringify(body)}}`,
    );
    navigateCallback();
  };
  const url = `${baseURL}/proxy/invitations/validation`;
  const headers = await getCustomHeaderAsync();
  if (!headers.Authorization) {
    goBack();
    return;
  }
  return http
    .post(url, body, {
      headers,
      params: {
        lang: mapLanguage(getI18n().language),
      },
    })
    .then((res: any) => {
      goBack();
    })
    .catch((err) => {
      const errors = get(err, "response.data.errors");
      const message = get(err, "response.data.message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    });
}

export const closeNotification = async (body: {
  messageIdList: [string];
  category: string;
}): Promise<any> => {
  try {
    const response = await http.patch(`${messagingUrl}/inboxes/me/click`, body, {
      headers: { ...getCustomHeader(), __loading: true },
    });
    return response;
  } catch (error: any) {
    return error.response.data;
  }
};

export async function reinstateVerify(body: any, survey: any) {
  const url = `${baseURL}/aob/recruiter/reinstate/verify?recruiterCode=${body.leader}&agentNumber=${body.recruiter}`;
  const headers = await getCustomHeaderAsync();
  return http
    .get(url, {
      headers,
    })
    .then((res: any) => {
      return res.data;
    })
    .catch((err) => {
      const errors = get(err, "response.data.errors");
      const message = get(err, "response.data.message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    });
}
export async function reinstateVerifyCandidate(body: any, survey: any) {
  const url = `${baseURL}/invitations/me?phone=${body.phoneNumber.replace(
    "+",
    "%2B",
  )}&locked=false`;
  const headers = await getCustomHeaderAsync();
  return http
    .get(url, {
      headers,
    })
    .then((res: any) => {
      return res.data;
    })
    .catch((err) => {
      const errors = get(err, "response.data.errors");
      const message = get(err, "response.data.message", "Unknown error");
      handleSurveyError({ errors, survey, defaultErrMsg: message });
    });
}
